import React, { useCallback, useEffect, useRef, useState } from "react";
import { UploadCloud, Download, Loader2, MapPin, AlertTriangle, RotateCcw, CheckCircle2, Search, XCircle, ChevronDown } from "lucide-react";
import {
  downloadLabPincodeTemplate,
  uploadLabPincodes,
  getLabPincodeUploadStatus,
  getLabPincodeVersions,
  rollbackLabPincodeVersion,
  LAB_PINCODE_UPLOAD_ACCEPT,
} from "../../services/diagnosticLabsService";

// Serviceable-pincode manager for National labs (replaces the address module).
// Replace-all with versioning: each upload supersedes the prior set; prior
// versions are retained for audit and one-click rollback. Frontend validates
// extension + size only — the backend owns pincode (6-digit) / dedupe / replace.
//
// Props: role, labId, readOnly (hide mutating controls), onToast(type, msg).
// Backend endpoints it depends on live in diagnosticLabsService.js.

const UPLOAD_MAX = 5 * 1024 * 1024; // 5 MB — a national pincode sheet is small
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

const LabPincodePanel = ({ role, labId, readOnly = false, onToast }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ active_version: null, versions: [], active_pincodes: [] });
  const [error, setError] = useState(null);
  const [pincodeQuery, setPincodeQuery] = useState("");
  const [showAllPincodes, setShowAllPincodes] = useState(false);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadReport, setUploadReport] = useState(null); // { added, invalid_count, error_file_url }
  const [rollbackBusy, setRollbackBusy] = useState(null); // version number in-flight

  const statusTimerRef = useRef(null);
  const toast = useCallback((type, msg) => onToast?.(type, msg), [onToast]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getLabPincodeVersions(role, labId);
      const payload = resp?.msg || resp || {};
      setData({
        active_version: payload.active_version ?? null,
        versions: Array.isArray(payload.versions) ? payload.versions : [],
        active_pincodes: Array.isArray(payload.active_pincodes) ? payload.active_pincodes : [],
      });
    } catch (err) {
      setError(err.message || "Failed to load serviceable pincodes.");
    } finally {
      setLoading(false);
    }
  }, [role, labId]);

  useEffect(() => {
    loadVersions();
    return () => {
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, [loadVersions]);

  const checkStatus = useCallback(
    async (documentId) => {
      try {
        const resp = await getLabPincodeUploadStatus(role, labId, documentId);
        const status = resp?.msg || resp || {};
        if (status.processing) {
          // still parsing — re-check once more shortly (one manual-style retry)
          statusTimerRef.current = setTimeout(() => checkStatus(documentId), 6000);
          return;
        }
        setProcessing(false);
        setUploadReport({
          added: status.added ?? status.count ?? null,
          invalid_count: status.invalid_count ?? 0,
          error_file_url: status.error_file_url || null,
        });
        if (status.invalid_count) {
          toast("error", `${status.invalid_count} invalid pincode row(s) were skipped — see the error report.`);
        } else {
          toast("success", "Serviceable pincodes updated.");
        }
        loadVersions();
      } catch (err) {
        setProcessing(false);
        setError(err.message || "Failed to check upload status.");
      }
    },
    [role, labId, toast, loadVersions]
  );

  const validFile = (file) => {
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!LAB_PINCODE_UPLOAD_ACCEPT.split(",").includes(ext)) {
      toast("error", "File must be an Excel/CSV (.xlsx, .xls, .csv).");
      return false;
    }
    if (file.size > UPLOAD_MAX) {
      toast("error", "File must be 5MB or smaller.");
      return false;
    }
    return true;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !validFile(file)) return;

    setUploadBusy(true);
    setUploadReport(null);
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    try {
      const resp = await uploadLabPincodes(role, labId, file);
      const body = resp?.msg || resp || {};
      const documentId = body.document_id;
      if (documentId) {
        setProcessing(true);
        statusTimerRef.current = setTimeout(() => checkStatus(documentId), 6000);
      } else {
        // synchronous backend — refresh straight away
        toast("success", "Serviceable pincodes updated.");
        loadVersions();
      }
    } catch (err) {
      toast("error", err.message || "Upload failed.");
    } finally {
      setUploadBusy(false);
    }
  };

  const handleTemplate = async () => {
    setTemplateBusy(true);
    try {
      const blob = await downloadLabPincodeTemplate(role);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "serviceable_pincodes_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast("error", err.message || "Failed to download template.");
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`Roll back to version ${version}? This replaces the currently active serviceable pincode set.`)) return;
    setRollbackBusy(version);
    try {
      await rollbackLabPincodeVersion(role, labId, version);
      toast("success", `Rolled back to version ${version}.`);
      loadVersions();
    } catch (err) {
      toast("error", err.message || "Rollback failed.");
    } finally {
      setRollbackBusy(null);
    }
  };

  const { active_version, versions, active_pincodes } = data;
  const activeVersion = versions.find((v) => v.version === active_version) || null;
  const activeCount = activeVersion?.count ?? 0;

  const query = pincodeQuery.trim();
  const matches = query ? active_pincodes.filter((p) => String(p).includes(query)) : [];
  const exactMatch = query.length === 6 && active_pincodes.includes(query);
  const PINCODE_DISPLAY_CAP = 500;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5">
        <span className="w-9 h-9 rounded-xl bg-[#0F4C81]/5 text-[#0F4C81] flex items-center justify-center shrink-0">
          <MapPin className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">Serviceable pincodes</p>
          <p className="text-xs text-gray-400">
            National labs serve by pincode instead of a single address. Uploading a sheet replaces the active
            set (versioned — prior sets are kept for audit &amp; rollback).
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleTemplate}
          disabled={templateBusy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {templateBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Download template
        </button>
        {!readOnly && (
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors">
            <input type="file" accept={LAB_PINCODE_UPLOAD_ACCEPT} className="hidden" disabled={uploadBusy || processing} onChange={handleUpload} />
            {uploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
            {activeVersion ? "Upload new set" : "Upload pincode sheet"}
          </label>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading serviceable pincodes…
        </div>
      ) : error ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-xs">
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {error}</span>
          <button type="button" onClick={loadVersions} className="font-medium underline shrink-0">Retry</button>
        </div>
      ) : !activeVersion ? (
        // Loud empty state — a national lab with no pincodes is invisible in serviceability.
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">No serviceable pincodes — this lab is not live.</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Upload a pincode sheet to make this national lab appear in serviceability checks and bookings.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-50 border border-gray-100">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900">{activeCount.toLocaleString()} serviceable pincodes</span>
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 text-[11px] font-medium">v{activeVersion.version} · active</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">Uploaded {fmtDate(activeVersion.uploaded_at)}</p>
          </div>
        </div>
      )}

      {/* Serviceable-pincode checker — search-first. The booking team's job is
          to verify ONE customer pincode, so lead with a lookup + clear verdict;
          the full list stays collapsed behind a toggle to avoid a chip wall. */}
      {activeVersion && active_pincodes.length > 0 && (
        <div className="rounded-xl border border-gray-100 overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pincodeQuery}
                onChange={(e) => setPincodeQuery(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="Enter a customer pincode to check serviceability"
                className="w-full pl-9 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-[#00A6A6] outline-none"
              />
              {query && (
                <button type="button" onClick={() => setPincodeQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500" aria-label="Clear">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Verdict */}
            {query && (
              exactMatch ? (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4 shrink-0" /> {query} is serviceable by this lab
                </div>
              ) : matches.length ? (
                <div className="mt-2.5">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    {matches.length} pincode{matches.length > 1 ? "s" : ""} start with “{query}”
                  </p>
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                    {matches.slice(0, 60).map((p) => (
                      <span key={p} className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-mono font-medium">{p}</span>
                    ))}
                  </div>
                  {matches.length > 60 && <p className="mt-1 text-[11px] text-gray-400">Type more digits to narrow down.</p>}
                </div>
              ) : (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-medium">
                  <XCircle className="w-4 h-4 shrink-0" /> {query} is not serviceable by this lab
                </div>
              )
            )}
          </div>

          {/* Browse all — collapsed by default */}
          {!query && (
            <>
              <button
                type="button"
                onClick={() => setShowAllPincodes((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <span>{showAllPincodes ? "Hide" : "View"} all {activeCount.toLocaleString()} pincodes</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllPincodes ? "rotate-180" : ""}`} />
              </button>
              {showAllPincodes && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 max-h-56 overflow-y-auto">
                    {active_pincodes.slice(0, PINCODE_DISPLAY_CAP).map((p) => (
                      <span key={p} className="px-2 py-1 rounded-md bg-gray-50 border border-gray-100 text-[11px] font-mono text-gray-700 text-center">{p}</span>
                    ))}
                  </div>
                  {active_pincodes.length > PINCODE_DISPLAY_CAP && (
                    <p className="mt-2 text-[11px] text-gray-400">
                      Showing first {PINCODE_DISPLAY_CAP.toLocaleString()} of {active_pincodes.length.toLocaleString()}. Use the search above to find a specific pincode.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Upload result (invalid rows) */}
      {uploadReport?.error_file_url && (
        <a
          href={uploadReport.error_file_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-900 hover:underline"
        >
          <AlertTriangle className="w-3 h-3 shrink-0" /> Some rows were invalid — download report
        </a>
      )}

      {processing && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-600 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Parsing the uploaded pincode sheet in the background…
        </div>
      )}

      {/* Version history + rollback */}
      {versions.length > 1 && (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">Version history</p>
          <ul className="space-y-1.5">
            {[...versions].sort((a, b) => b.version - a.version).slice(0, 5).map((v) => {
              const isActive = v.version === active_version;
              return (
                <li key={v.version} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-100 text-sm">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-gray-700 truncate flex-1">
                    {typeof v.count === "number" ? `${v.count.toLocaleString()} pincodes` : `Version ${v.version}`}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-md text-[11px] font-medium ${isActive ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"}`}>
                    v{v.version}{isActive ? " · active" : ""}
                  </span>
                  <span className="text-[11px] text-gray-400 hidden sm:block">{fmtDate(v.uploaded_at)}</span>
                  {!readOnly && !isActive && (
                    <button
                      type="button"
                      onClick={() => handleRollback(v.version)}
                      disabled={rollbackBusy != null}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-50 shrink-0 transition-colors"
                    >
                      {rollbackBusy === v.version ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      Rollback
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {versions.length > 5 && (
            <p className="mt-2 text-[11px] text-gray-400">Showing latest 5 of {versions.length} versions.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default LabPincodePanel;
